import React, { useRef, useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import styled, { css } from 'styled-components';
import { hash } from '@/utils/cache';

type LoadStatus = `loading` | `loaded` | `failed`;

export interface DynamicComponentProps {
  url: string;
  className?: string;
  minHeight?: string;
  fadeIn?: boolean;
  fadeInDuration?: string;
  collapseDuration?: string;
  onSuccess?: (html: string, url: string) => void;
  onError?: (error: Error, url: string) => void;
}

/**
 * Fetches the markup served from `url`.
 * @param url - Endpoint returning the html fragment to inject
 * @returns The response body as text
 * @throws When the response status is not ok
 */
async function fetchHtml(url: string) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
  }
  return res.text();
}

function toError(reason: unknown) {
  return reason instanceof Error ? reason : new Error(String(reason));
}

export function DynamicComponent({
  url,
  className,
  minHeight,
  fadeIn = false,
  fadeInDuration = `0.4s`,
  collapseDuration = `0.4s`,
  onSuccess,
  onError,
}: DynamicComponentProps) {
  const [status, setStatus] = useState<LoadStatus>(`loading`);
  const [html, setHtml] = useState<string | undefined>(undefined);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useAsyncEffect(
    async (isMounted) => {
      setHtml(undefined);
      setStatus(`loading`);

      try {
        const fetchedHtml = await fetchHtml(url);
        if (!isMounted()) {
          return;
        }
        setHtml(fetchedHtml);
        setStatus(`loaded`);
        onSuccessRef.current?.(fetchedHtml, url);
      } catch (reason) {
        if (!isMounted()) {
          return;
        }
        setHtml(undefined);
        setStatus(`failed`);
        onErrorRef.current?.(toError(reason), url);
      }
    },
    [url],
  );

  const isLoaded = status === `loaded`;

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isLoaded && html && (
        <>
          <Content
            id={`dyn-${hash(url)}`}
            className={`dynamic-component ${className}`}
            $fadeIn={fadeIn}
            $fadeInDuration={fadeInDuration}
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <div style={{ opacity: `0`, height: `0px` }} />
        </>
      )}
      {minHeight && (
        <Placeholder
          $minHeight={minHeight}
          $isCollapsed={status !== `loading`}
          $collapseDuration={collapseDuration}
        />
      )}
    </>
  );
}

const Placeholder = styled.div<{
  $minHeight: string;
  $isCollapsed: boolean;
  $collapseDuration: string;
}>`
  width: 100%;
  min-height: ${({ $isCollapsed, $minHeight }) =>
    $isCollapsed ? `0px` : $minHeight};
`;

const Content = styled.div<{ $fadeIn: boolean; $fadeInDuration: string }>`
  @keyframes dynamicComponentFadeIn {
    to {
      opacity: 1;
    }
  }

  ${({ $fadeIn, $fadeInDuration }) =>
    $fadeIn &&
    css`
      && {
        opacity: 0;
        animation: dynamicComponentFadeIn ${$fadeInDuration} ease-in-out
          forwards;
      }
    `}
`;
